/** Bảng điểm & tiến bộ — `S.score` của bản mẫu (spec 09). */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ProfileInput, type Account } from "@ca/shared";
import { Button, Card, CardHead, ErrorState, Modal, PageHead } from "@ca/ui";
import { api, loadMe } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { ShareButton } from "./community.tsx";
import { useScores } from "./side-counts.ts";
import { useGo } from "./study.tsx";

const Insights = z.object({
  targets: z.object({
    goal: z.number(),
    lc: z.number(),
    rc: z.number(),
    estimatedScore: z.number(),
    parts: z.array(z.number()),
    part7: z.object({ single: z.number(), multi: z.number() }).optional(),
  }),
  change: z.number(),
  priorityPart: z.number().nullable(),
});
const GOALS = [450, 550, 600, 650, 700, 750, 800, 860, 900, 990];
const NEED_ROWS: [string, string, number][] = [
  ["LC", "Part 1 · Tranh", 6],
  ["", "Part 2 · Hỏi đáp", 25],
  ["", "Part 3 · Hội thoại", 39],
  ["", "Part 4 · Độc thoại", 30],
  ["RC", "Part 5 · Điền từ vào câu", 30],
  ["", "Part 6 · Điền vào đoạn", 16],
  ["", "Part 7 · Đoạn đơn", 29],
  ["", "Part 7 · Đoạn đôi, ba", 25],
];
const ddmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};
function roundTop(x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, h, w / 2));
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}
/** Cột chồng Nghe + Đọc theo từng lần full test, đường ngang là mục tiêu (`scoreChart`). */
function ScoreChart({
  rows,
  goal,
}: {
  rows: { id: string; date: string; name: string; lc: number; rc: number }[];
  goal: number;
}) {
  const W = 720,
    H = 250,
    L = 40,
    R = 14,
    T = 20,
    B = 30,
    pw = W - L - R,
    ph = H - T - B,
    slot = pw / Math.max(rows.length, 4),
    bw = 26;
  const y = (v: number) => T + ph - (v / 1000) * ph;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Điểm các lần thi full test, cột chồng Nghe và Đọc"
    >
      {[0, 200, 400, 600, 800, 1000].map((v) => (
        <g key={v}>
          <line className={v ? "c-grid" : "c-base"} x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
          <text className="c-lbl" x={L - 8} y={y(v) + 4} textAnchor="end">
            {v}
          </text>
        </g>
      ))}
      {rows.map((s, i) => {
        const cx = L + slot * i + slot / 2,
          x = cx - bw / 2,
          tot = s.lc + s.rc;
        return (
          <g key={s.id} className="colg mark">
            <rect
              className="f1"
              x={x}
              y={y(s.lc) + 2}
              width={bw}
              height={Math.max(0, y(0) - y(s.lc) - 2)}
            />
            <path className="f2" d={roundTop(x, y(tot), bw, y(s.lc) - y(tot), 4)} />
            <rect
              className="col-hit"
              x={L + slot * i}
              y={T}
              width={slot}
              height={ph}
              tabIndex={0}
              data-tip={`${s.date} · ${s.name} · ${tot} điểm|#1 Nghe (Part 1–4): ${s.lc}|#2 Đọc (Part 5–7): ${s.rc}`}
            />
          </g>
        );
      })}
      <line
        className="c-goal"
        x1={L}
        x2={W - R}
        y1={y(goal)}
        y2={y(goal)}
        style={{ pointerEvents: "none" }}
      />
      {rows.map((s, i) => {
        const cx = L + slot * i + slot / 2;
        return (
          <g key={`l${s.id}`}>
            <text className="c-val" x={cx} y={y(s.lc + s.rc) - 8} textAnchor="middle">
              {s.lc + s.rc}
            </text>
            <text
              className={`c-lbl${i === rows.length - 1 ? " strong" : ""}`}
              x={cx}
              y={H - 9}
              textAnchor="middle"
            >
              {s.date}
            </text>
          </g>
        );
      })}
      <text className="c-goal-lbl" x={L + 6} y={y(goal) - 6} textAnchor="start">
        Mục tiêu {goal}
      </text>
    </svg>
  );
}
export function Scoreboard({ account, notify }: { account: Account; notify: (s: string) => void }) {
  const go = useGo(),
    cache = useQueryClient(),
    history = useScores(account.id),
    insights = useQuery({
      queryKey: ["insights", account.id],
      queryFn: async () => Insights.parse(await api("/v1/me/scores/insights")),
    });
  const [hide, setHide] = useState<string | null>(null),
    [goalText, setGoalText] = useState(""),
    [saving, setSaving] = useState(false);
  const full = (history.data ?? []).filter((s) => s.result.totalScore !== null);
  const ascending = [...full].reverse();
  const goal = insights.data?.targets.goal ?? account.profile.goalScore ?? 600;
  const t = insights.data?.targets;
  async function hideRow() {
    if (!hide) return;
    try {
      await api(`/v1/me/scores/${hide}`, {
        method: "DELETE",
        key: uuidv7(),
        accountId: account.id,
      });
      setHide(null);
      await history.refetch();
      await insights.refetch();
    } catch (e) {
      notify(String(e));
    }
  }
  async function changeGoal(next: number) {
    if (!Number.isFinite(next) || next < 10 || next > 990 || next === goal) return;
    setSaving(true);
    try {
      const me = await loadMe();
      await api("/v1/me/profile", {
        method: "PUT",
        accountId: account.id,
        key: uuidv7(),
        body: ProfileInput.parse({
          expectedRev: me.profile.rev,
          displayName: me.profile.displayName ?? me.name ?? "Bạn",
          goalScore: Math.round(next / 5) * 5,
          currentLevel: me.profile.currentLevel ?? "basic",
          dailyGoalMinutes: me.profile.dailyGoalMinutes,
          studySlot: me.profile.studySlot ?? { from: "20:00", to: "21:00" },
          examDate: me.profile.examDate ?? null,
        }),
      });
      setGoalText("");
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["me"] }),
        cache.invalidateQueries({ queryKey: ["insights", account.id] }),
        cache.invalidateQueries({ queryKey: ["revision", account.id] }),
      ]);
      notify(`Đã đổi mục tiêu thành ${Math.round(next / 5) * 5} điểm.`);
    } catch (e) {
      notify(e instanceof Error ? e.message : "Chưa đổi được mục tiêu.");
    } finally {
      setSaving(false);
    }
  }
  const latest = full[0];
  return (
    <>
      <PageHead
        space="study"
        title="Bảng điểm & tiến bộ"
        desc="Tự động ghi sau mỗi lần làm full test (đủ Part 1–4 Nghe và Part 5–7 Đọc). Làm lẻ từng part không tính vào bảng này."
      />
      <Card>
        <CardHead
          title="📈 Điểm theo thời gian"
          sub={`${full.length} lần full test · cột chồng Nghe + Đọc · đường ngang là mục tiêu${insights.data?.change ? ` · ${insights.data.change > 0 ? "+" : ""}${insights.data.change} điểm so với lần đầu` : ""}`}
        >
          {latest && (
            <ShareButton
              uid={account.id}
              notify={notify}
              input={{ kind: "score", attemptId: latest.id }}
            />
          )}
        </CardHead>
        <div className="legend">
          <span>
            <i className="b1" />
            Nghe · Part 1–4
          </span>
          <span>
            <i className="b2" />
            Đọc · Part 5–7
          </span>
        </div>
        <div className="chart-scroll">
          <div className="chart">
            <ScoreChart
              goal={goal}
              rows={ascending.map((s) => ({
                id: s.id,
                date: ddmm(s.submittedAt),
                name: s.name,
                lc: s.result.lcScore ?? 0,
                rc: s.result.rcScore ?? 0,
              }))}
            />
          </div>
        </div>
        {!full.length && (
          <p className="faint" style={{ fontSize: 13, fontWeight: 600, textAlign: "center" }}>
            Hoàn tất 1 đề full để có điểm đầu tiên.
          </p>
        )}
        {history.isError ? (
          <ErrorState
            message="Chưa tải được bảng điểm."
            onRetry={() => {
              void history.refetch();
            }}
          />
        ) : history.data?.length ? (
          <div className="tbl-wrap" style={{ marginTop: 14 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th>Đề</th>
                  {["P1", "P2", "P3", "P4", "P5", "P6", "P7"].map((p) => (
                    <th key={p} className="n">
                      {p}
                    </th>
                  ))}
                  <th className="n">LC</th>
                  <th className="n">RC</th>
                  <th className="n">Tổng</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.data.map((s) => (
                  <tr key={s.id}>
                    <td>{new Date(s.submittedAt).toLocaleDateString("vi-VN")}</td>
                    <td>
                      <button
                        className="note-t"
                        style={{ fontSize: 13.5, fontWeight: 700 }}
                        onClick={() => {
                          go(`/attempts/${s.id}`);
                        }}
                      >
                        {s.name}
                      </button>
                    </td>
                    {[1, 2, 3, 4, 5, 6, 7].map((p) => (
                      <td key={p} className="n">
                        {s.result.parts.find((x) => x.part === p)?.correct ?? "—"}
                      </td>
                    ))}
                    <td className="n">{s.result.lcScore ?? "—"}</td>
                    <td className="n">{s.result.rcScore ?? "—"}</td>
                    <td className="n">
                      <b>{s.result.totalScore ?? `${s.result.correct}/${s.result.total}`}</b>
                    </td>
                    <td>
                      <Button
                        variant="soft"
                        size="sm"
                        onClick={() => {
                          setHide(s.id);
                        }}
                      >
                        Ẩn dòng
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
      <Card className="stack">
        <div>
          <h2>🎯 Mục tiêu điểm số của bạn</h2>
          <p className="sub">
            Nhập điểm muốn đạt, Cá tính ngược ra số câu tối thiểu phải đúng ở từng part theo bảng
            quy đổi TOEIC.
          </p>
        </div>
        <div className="row">
          <input
            className="input"
            style={{ width: 110, fontSize: 22, fontWeight: 800, textAlign: "center", height: 52 }}
            inputMode="numeric"
            aria-label="Điểm mục tiêu"
            disabled={saving}
            value={goalText || String(goal)}
            onChange={(e) => {
              setGoalText(e.target.value.replace(/\D/g, "").slice(0, 3));
            }}
            onBlur={() => {
              if (goalText) void changeGoal(Number(goalText));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && goalText) void changeGoal(Number(goalText));
            }}
          />
          <span className="faint" style={{ fontWeight: 800 }}>
            / 990
          </span>
          {GOALS.map((v) => (
            <button
              key={v}
              className={`chip${v === goal ? " dark" : ""}`}
              disabled={saving}
              onClick={() => {
                void changeGoal(v);
              }}
            >
              {v}
            </button>
          ))}
        </div>
        {t && (
          <div className="kpi" style={{ background: "var(--accent-soft)" }}>
            <b>
              Để chạm {t.goal} điểm, bạn cần đúng tối thiểu {t.lc + t.rc}/200 câu: Nghe {t.lc}/100
              và Đọc {t.rc}/100 (ước tính {t.estimatedScore} điểm).
            </b>
            <span className="k-sub">
              Được phép sai: Nghe {100 - t.lc} câu · Đọc {100 - t.rc} câu.
            </span>
          </div>
        )}
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Kỹ năng</th>
                <th>Part</th>
                <th className="n">Số câu</th>
                <th className="n">Cần đúng</th>
              </tr>
            </thead>
            <tbody>
              {NEED_ROWS.map(([skill, part, n], i) => (
                <tr key={part}>
                  <td>
                    <b>{skill}</b>
                  </td>
                  <td>{part}</td>
                  <td className="n">{n}</td>
                  <td className="n">
                    <b>
                      {(i < 6 ? t?.parts[i] : i === 6 ? t?.part7?.single : t?.part7?.multi) ?? "—"}
                    </b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
          Gợi ý số câu đúng theo bảng quy đổi; điểm thi thực tế có thể khác.
        </p>
      </Card>
      {hide && (
        <Modal
          title="Ẩn khỏi bảng điểm?"
          className="sm"
          onClose={() => {
            setHide(null);
          }}
        >
          <p className="muted">Bài làm và câu sai vẫn được giữ lại.</p>
          <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setHide(null);
              }}
            >
              Huỷ
            </Button>
            <Button
              onClick={() => {
                void hideRow();
              }}
            >
              Ẩn dòng này
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
